// apps/web/src/pages/auth/LinkExisting.tsx
//
// Plan 04-12b Task 2 — AUTH-12 link-existing re-auth UI.
//
// CONTEXT D-06 flow (Plan 04-07):
//   1. User initiated OAuth → provider returned email that collides with an
//      existing local account.
//   2. The Plan 04-07 hook REFUSED the auto-link and redirected the browser to
//      /auth/link-existing?provider=X&email=Y&challenge_token=<...>.
//   3. This page asks the user to re-authenticate to the existing account
//      (password + MFA when enrolled), then POSTs the challenge_token to
//      /api/auth/link-existing/complete to finalize the linkage.
//
// SECURITY (T-04-12b-03 / T1):
//   The challenge_token is in the URL query string. This is documented as
//   acceptable: the token is only meaningful to the user who can authenticate
//   to the existing account, the TTL is 15 min, and completion requires
//   password + MFA. URL-bar exposure is the trade-off for a redirect-based
//   flow.

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { api, ApiError } from "@/lib/api-client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordField } from "@/components/forms/EmailPasswordFields";

const linkSchema = z.object({
  password: z.string().min(1, "Password is required"),
  mfaCode: z.string().optional(),
});

type LinkFormValues = z.infer<typeof linkSchema>;

const PROVIDER_LABELS: Record<string, string> = {
  google: "Google",
  github: "GitHub",
};

export function LinkExisting(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const provider = searchParams.get("provider") ?? "";
  const email = searchParams.get("email") ?? "";
  const challengeToken = searchParams.get("challenge_token") ?? "";

  const [mfaRequired, setMfaRequired] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<LinkFormValues>({
    resolver: zodResolver(linkSchema),
    defaultValues: { password: "", mfaCode: "" },
    mode: "onSubmit",
  });

  async function onSubmit(values: LinkFormValues): Promise<void> {
    setServerError(null);
    try {
      const result = await api<{
        ok: true;
        userId: string;
        provider: string;
        providerAccountId: string;
      }>("/api/auth/link-existing/complete", {
        method: "POST",
        body: JSON.stringify({
          challenge_token: challengeToken,
          password: values.password,
          mfa_code:
            values.mfaCode !== undefined && values.mfaCode.length > 0
              ? values.mfaCode
              : undefined,
        }),
      });
      if (result.ok === true) {
        navigate("/");
        return;
      }
    } catch (err) {
      if (err instanceof ApiError) {
        type ErrBody = { ok?: boolean; reason?: string };
        const body = err.body as ErrBody | null;
        const reason = body?.reason;
        if (reason === "MFA_REQUIRED" || reason === "INVALID_MFA_CODE") {
          setMfaRequired(true);
          setServerError(
            reason === "INVALID_MFA_CODE"
              ? "That code didn't match. Try again."
              : "Enter your authenticator code to confirm.",
          );
          return;
        }
        if (reason === "INVALID_PASSWORD" || reason === "INVALID_CHALLENGE") {
          setServerError("Authentication failed");
          return;
        }
        if (reason === "CHALLENGE_EXPIRED") {
          setServerError(
            "This link has expired. Try signing in again from the start.",
          );
          return;
        }
        setServerError("Could not link the account. Please try again.");
        return;
      }
      setServerError(err instanceof Error ? err.message : "Could not link account");
    }
  }

  if (challengeToken === "" || provider === "" || email === "") {
    return (
      <main className="mx-auto max-w-md p-8">
        <Card>
          <CardHeader>
            <CardTitle>Missing link information</CardTitle>
            <CardDescription>
              Invalid link. Please start the sign-in flow again.
            </CardDescription>
          </CardHeader>
        </Card>
      </main>
    );
  }

  const providerLabel = PROVIDER_LABELS[provider] ?? provider;

  return (
    <main className="mx-auto max-w-md p-8">
      <Card>
        <CardHeader>
          <CardTitle>Confirm your account</CardTitle>
          <CardDescription>
            A {providerLabel} account with email <strong>{email}</strong> is
            being linked to your existing account. Confirm by entering your
            existing account's password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
            >
              {serverError !== null ? (
                <Alert variant={mfaRequired ? "default" : "destructive"}>
                  <AlertTitle>
                    {mfaRequired ? "Two-factor required" : "Could not link"}
                  </AlertTitle>
                  <AlertDescription>{serverError}</AlertDescription>
                </Alert>
              ) : null}

              <PasswordField
                control={form.control}
                name="password"
                label="Password"
                autoComplete="current-password"
                disabled={form.formState.isSubmitting}
              />

              {mfaRequired ? (
                <FormField
                  control={form.control}
                  name="mfaCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Authenticator code</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          placeholder="123456"
                          maxLength={6}
                          disabled={form.formState.isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Enter the 6-digit code from your authenticator app.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              <Button
                type="submit"
                className="w-full"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? "Confirming…" : "Confirm and link"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </main>
  );
}
